import { useEffect, useRef } from "react";
import type { SSENotification } from "./useNotificationsSSE";
import { useNotifyToast } from "@/contexts/NotificationsCtx";
import { useTranslation } from "react-i18next";
import {
  getNotificationDisplayBody,
  isNotificationVisible,
} from "@/lib/notifications";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

export function useSseNotificationToasts({
  notifications,
}: {
  notifications?: SSENotification[];
}) {
  const { t } = useTranslation();
  const { notifyMany } = useNotifyToast();
  const { capabilities } = useAccessCapabilities();
  const shown = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!notifications?.length) return;

    const fresh = notifications
      .filter(
        (n) =>
          n?.id &&
          !shown.current.has(n.id) &&
          isNotificationVisible(n, capabilities.canUseRequestsSystem),
      )
      .map((n) => {
        shown.current.add(n.id);

        return {
          notification: n,
          title: n.title ?? t("notifications.newFallbackTitle"),
          description: getNotificationDisplayBody(n, t),
        };
      });

    if (fresh.length === 0) return;

    notifyMany(fresh, { max: 6 });
  }, [capabilities.canUseRequestsSystem, notifications, notifyMany, t]);
}
